import axiosInstance from '../../services/api';

/**
 * Generic Share API Service
 * Handles all API calls related to shareable content
 */
export class ShareService {
  /**
   * Creates a new share
   * @param {Object} params - Share creation parameters
   * @param {string} params.contentType - Type of content (e.g., 'risk_calculator')
   * @param {Object} params.data - Data to store (structure depends on contentType)
   * @param {string} [params.title] - Optional title for the share
   * @returns {Promise<Object>} Created share with token
   */
  static async createShare({ contentType, data, title = '' }) {
    try {
      const response = await axiosInstance.post('/api/shares/', {
        content_type: contentType,
        data,
        title,
      });
      return response.data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error creating share:', error);
      throw error;
    }
  }

  /**
   * Retrieves a share by token
   * @param {string} token - Share token
   * @returns {Promise<Object>} Share data including stored content
   */
  static async getShare(token) {
    try {
      const response = await axiosInstance.get(`/api/shares/${token}/`);
      return response.data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching share:', error);
      throw error;
    }
  }

  /**
   * Lists shares created by the current user
   * @param {string} [contentType] - Optional filter by content type
   * @returns {Promise<Object>} Paginated list of shares
   */
  static async listShares(contentType = null) {
    try {
      const params = contentType ? { content_type: contentType } : {};
      const response = await axiosInstance.get('/api/shares/list/', { params });
      return response.data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error listing shares:', error);
      throw error;
    }
  }

  /**
   * Soft deletes a share (owner only)
   * @param {string} token - Share token to delete
   * @returns {Promise<void>}
   */
  static async deleteShare(token) {
    try {
      await axiosInstance.delete(`/api/shares/${token}/delete/`);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error deleting share:', error);
      throw error;
    }
  }
}

/**
 * Query keys factory for React Query cache management
 * Provides consistent keys for caching and invalidation
 */
export const shareKeys = {
  all: ['shares'],
  lists: () => [...shareKeys.all, 'list'],
  list: (contentType) => [...shareKeys.lists(), contentType],
  details: () => [...shareKeys.all, 'detail'],
  detail: (token) => [...shareKeys.details(), token],
};
